<template>
  <section
    class="personal-center"
    :class="{ 'up-page': !embeddedMobile, 'personal-center--embedded-mobile': embeddedMobile }"
  >
    <template v-if="embeddedMobile">
      <div v-if="mobileView === 'api'" class="mobile-account-view mobile-account-view--api">
        <h2>{{ $t('交易所 API 绑定') }}</h2>
        <p class="mobile-account-view__subtitle">
          {{ boundApiCards.length }} / {{ apiCards.length }} {{ $t('已绑定') }} · {{ $t('只授权交易，不授权提币') }}
        </p>

        <section
          v-for="card in apiCards"
          :key="`mobile-api-${card.robotType}`"
          :class="['mobile-api-card', { 'is-bound': card.bound }]"
        >
          <div class="mobile-api-card__head">
            <img :src="card.logo" :alt="card.displayName" />
            <div class="mobile-api-card__identity">
              <div>
                <strong>{{ card.displayName }}</strong>
                <span :class="{ 'is-bound': card.bound }">{{ $t(card.bound ? '已绑定' : '未绑定') }}</span>
              </div>
              <small>{{ card.bound ? card.boundDescription : card.unboundDescription }}</small>
            </div>
            <button type="button" @click="openApi(card, card.bound)">
              {{ $t(card.bound ? '重新绑定' : '立即绑定') }}
            </button>
          </div>

          <div v-if="card.bound" class="mobile-api-card__details">
            <div>
              <small>{{ $t('授权范围') }}</small>
              <strong>{{ $t('现货 · 合约交易') }}</strong>
            </div>
            <div>
              <small>{{ $t('运行中策略') }}</small>
              <strong>{{ robotUsageCounts[card.robotType] == null ? '--' : `${robotUsageCounts[card.robotType]} ${$t('个')}` }}</strong>
            </div>
            <button type="button" @click="requestUnbind(card)">{{ $t('解绑') }}</button>
          </div>
        </section>

        <div v-if="!apiCards.length" class="mobile-account-view__empty">{{ $t('暂无可绑定的交易所') }}</div>
        <div class="mobile-account-view__notice">
          {{ $t('绑定时请勿开启提币权限；建议同时配置 IP 白名单以提升安全性。') }}
        </div>
      </div>

      <div v-else-if="mobileView === 'personal-info'" class="mobile-account-view mobile-account-view--profile">
        <h2>{{ $t('个人资料') }}</h2>
        <div
          v-for="row in mobileAccountRows"
          :key="row.label"
          class="mobile-profile-row"
        >
          <span>{{ $t(row.label) }}</span>
          <strong :class="{ 'is-accent': row.accent }">{{ row.value }}</strong>
        </div>
        <button
          v-if="invitation"
          class="mobile-profile-copy"
          type="button"
          @click="copyInvitation"
        >
          {{ $t('复制我的邀请码') }}
        </button>
      </div>

      <form
        v-else-if="mobileView === 'password'"
        class="mobile-account-view mobile-account-view--password"
        @submit.prevent="savePassword"
      >
        <h2>{{ passwordCopy.dialogTitle }}</h2>
        <label class="personal-field">
          <span>{{ passwordCopy.oldPassword }}</span>
          <span class="personal-password-field">
            <input
              v-model="passwordForm.oldPassword"
              :type="passwordFieldVisible.oldPassword ? 'text' : 'password'"
              autocomplete="current-password"
              :placeholder="passwordCopy.oldPlaceholder"
            />
            <button
              class="personal-password-field__toggle"
              :class="{ active: passwordFieldVisible.oldPassword }"
              type="button"
              :aria-label="passwordFieldVisible.oldPassword ? passwordCopy.hideOld : passwordCopy.showOld"
              @click.prevent.stop="passwordFieldVisible.oldPassword = !passwordFieldVisible.oldPassword"
            >
              <PasswordVisibilityIcon :visible="passwordFieldVisible.oldPassword" />
            </button>
          </span>
        </label>
        <label class="personal-field">
          <span>{{ passwordCopy.newPassword }}</span>
          <span class="personal-password-field">
            <input
              v-model="passwordForm.newPassword"
              :type="passwordFieldVisible.newPassword ? 'text' : 'password'"
              autocomplete="new-password"
              maxlength="32"
              :placeholder="passwordCopy.newPlaceholder"
            />
            <button
              class="personal-password-field__toggle"
              :class="{ active: passwordFieldVisible.newPassword }"
              type="button"
              :aria-label="passwordFieldVisible.newPassword ? passwordCopy.hideNew : passwordCopy.showNew"
              @click.prevent.stop="passwordFieldVisible.newPassword = !passwordFieldVisible.newPassword"
            >
              <PasswordVisibilityIcon :visible="passwordFieldVisible.newPassword" />
            </button>
          </span>
          <span
            v-if="passwordForm.newPassword"
            class="personal-password-strength"
            :class="`is-level-${passwordStrengthLevel}`"
          >
            <span class="personal-password-strength__bars" aria-hidden="true">
              <i v-for="level in 3" :key="level" :class="{ active: level <= passwordStrengthLevel }" />
            </span>
            <em>{{ passwordCopy.strength }}：{{ passwordStrengthText }}</em>
          </span>
          <small v-if="passwordForm.newPassword && !passwordFormatValid" class="personal-password-error">
            {{ passwordCopy.requirement }}
          </small>
        </label>
        <label class="personal-field">
          <span>{{ passwordCopy.confirmPassword }}</span>
          <span class="personal-password-field">
            <input
              v-model="passwordForm.confirmPassword"
              :type="passwordFieldVisible.confirmPassword ? 'text' : 'password'"
              autocomplete="new-password"
              maxlength="32"
              :placeholder="passwordCopy.confirmPlaceholder"
            />
            <button
              class="personal-password-field__toggle"
              :class="{ active: passwordFieldVisible.confirmPassword }"
              type="button"
              :aria-label="passwordFieldVisible.confirmPassword ? passwordCopy.hideConfirm : passwordCopy.showConfirm"
              @click.prevent.stop="passwordFieldVisible.confirmPassword = !passwordFieldVisible.confirmPassword"
            >
              <PasswordVisibilityIcon :visible="passwordFieldVisible.confirmPassword" />
            </button>
          </span>
          <small v-if="passwordConfirmMismatch" class="personal-password-error">{{ passwordCopy.mismatch }}</small>
        </label>
        <button class="mobile-password-submit" type="submit" :disabled="passwordSubmitting">
          {{ passwordSubmitting ? passwordCopy.submitting : passwordCopy.confirm }}
        </button>
      </form>
    </template>

    <template v-else>
    <header class="personal-center__heading">
      <h1>个人中心</h1>
      <p>个人资料、账户安全与交易所 API 绑定</p>
    </header>

    <div class="personal-center__layout">
      <div class="personal-center__side">
        <article class="personal-card personal-profile">
          <div class="personal-profile__summary">
            <UserAvatar
              class="personal-profile__avatar"
              :src="avatarUrl"
              :initial="avatarInitial"
            />
            <div class="personal-profile__identity">
              <div>
                <strong>{{ displayName }}</strong>
                <span v-if="membershipTag" class="personal-profile__pro">{{ membershipTag }}</span>
              </div>
              <small>
                <template v-if="registerDate">注册于 {{ registerDate }}</template>
              </small>
            </div>
            <button class="personal-button personal-button--ghost" type="button" @click="openProfile">
              编辑资料
            </button>
          </div>

          <dl class="personal-profile__details">
            <div>
              <dt>邮箱</dt>
              <dd>{{ maskedEmail || '暂未绑定' }}</dd>
            </div>
            <div>
              <dt>手机</dt>
              <dd>{{ maskedMobile || '暂未绑定' }}</dd>
            </div>
            <div>
              <dt>会员等级</dt>
              <dd :class="{ 'is-member': membershipLabel !== '--' }">{{ membershipLabel }}</dd>
            </div>
            <div>
              <dt>邀请码</dt>
              <dd class="personal-profile__invitation">
                <b>{{ invitation || '--' }}</b>
                <button v-if="invitation" type="button" @click="copyInvitation">复制</button>
              </dd>
            </div>
          </dl>
        </article>

        <article class="personal-card personal-security">
          <h2>{{ passwordCopy.accountSecurity }}</h2>
          <div class="personal-security__item">
            <div>
              <strong>{{ passwordCopy.loginPassword }}</strong>
              <small>{{ passwordCopy.configured }}</small>
            </div>
            <button class="personal-button personal-button--ghost" type="button" @click="openPassword">
              {{ passwordCopy.change }}
            </button>
          </div>
          <!-- 防钓鱼码设置模块暂时停用，后续需要时在此处恢复。 -->
          <div v-if="formattedLastLogin" class="personal-security__last-login">
            {{ passwordCopy.lastLogin }}：{{ formattedLastLogin }}
          </div>
        </article>
      </div>

      <article id="profile-api-section" class="personal-card personal-api">
        <div class="personal-api__heading">
          <div>
            <h2>交易所 API 绑定</h2>
            <p>只读 + 交易授权，无提现权限，资产始终留在你自己的交易所账户</p>
          </div>
        </div>

        <template v-if="apiCards.length">
          <div class="personal-api__support">
            <strong>支持交易所</strong>
            <div class="personal-api__support-list">
              <span
                v-for="card in apiCards"
                :key="`support-${card.robotType}`"
                :class="['personal-api__support-item', { 'is-bound': card.bound }]"
              >
                <img :src="card.logo" alt="" />
                <b>{{ card.displayName }}</b>
                <i />
              </span>
            </div>
            <b class="personal-api__bound-count">{{ boundApiCards.length }} / {{ apiCards.length }} 已绑定</b>
          </div>

          <div v-if="boundApiCards.length" class="personal-api__bound-list">
            <section
              v-for="card in boundApiCards"
              :key="`bound-${card.robotType}`"
              class="personal-api__card personal-api__card--bound"
            >
              <div class="personal-api__card-main">
                <span :class="['personal-api__exchange-logo', `is-exchange-${card.robotType}`]">
                  <img :src="card.logo" alt="" />
                </span>
                <div class="personal-api__exchange-copy">
                  <div>
                    <strong>{{ card.displayName }}</strong>
                    <span class="personal-api__bound"><i />已绑定</span>
                  </div>
                  <small>{{ card.boundDescription }}</small>
                </div>
                <div class="personal-api__actions">
                  <button class="personal-button personal-button--ghost" type="button" @click="openApi(card, true)">
                    重新绑定
                  </button>
                  <button class="personal-button personal-button--danger" type="button" @click="requestUnbind(card)">
                    解绑
                  </button>
                </div>
              </div>

              <div class="personal-api__permissions">
                <span class="personal-api__key">
                  <em>API Key</em>
                  <b v-if="card.maskedApiKey">{{ card.maskedApiKey }}</b>
                  <b v-else>已加密保存</b>
                </span>
                <div>
                  <b>只读</b>
                  <b>交易</b>
                  <b class="disabled">禁止提现</b>
                </div>
              </div>
            </section>
          </div>

          <div v-if="bindableApiCards.length" class="personal-api__unbound-grid">
            <section
              v-for="card in bindableApiCards"
              :key="`unbound-${card.robotType}`"
              class="personal-api__unbound-card"
            >
              <div class="personal-api__unbound-heading">
                <span :class="['personal-api__exchange-logo', `is-exchange-${card.robotType}`]">
                  <img :src="card.logo" alt="" />
                </span>
                <strong>{{ card.displayName }}</strong>
                <span>未绑定</span>
              </div>
              <p>{{ card.unboundDescription }}</p>
              <button type="button" @click="openApi(card, false)">立即绑定 <i>›</i></button>
            </section>
          </div>
        </template>
        <div v-else class="personal-api__empty">暂无可绑定的交易所</div>

        <div class="personal-api__notice">
          <i class="el-icon-circle-check" aria-hidden="true" />
          <span>密钥经 AES-256 加密存储，仅用于查询与下单，平台无法提现你的资产；建议在交易所侧同时设置 IP 白名单。</span>
        </div>
      </article>
    </div>
    </template>

    <transition name="personal-modal-fade">
      <div v-if="profileVisible" class="personal-modal" role="dialog" aria-modal="true" aria-label="编辑资料">
        <div class="personal-modal__backdrop" @click.stop />
        <form class="personal-modal__panel" @submit.prevent="saveProfile">
          <div class="personal-modal__title">
            <h3>编辑资料</h3>
            <button type="button" aria-label="关闭" @click="closeProfile">×</button>
          </div>
          <label class="personal-field__label">头像</label>
          <div class="profile-editor__avatar-row">
            <UserAvatar
              class="profile-editor__avatar"
              :src="profileAvatarPreview || avatarUrl"
              :initial="avatarInitial"
            />
            <div>
              <input
                ref="avatarInput"
                class="profile-editor__file"
                type="file"
                accept="image/jpeg,image/png"
                @change="selectAvatar"
              />
              <button class="personal-button personal-button--ghost" type="button" @click="$refs.avatarInput.click()">
                选择图片
              </button>
              <p>支持 JPG / PNG，文件不超过 4MB</p>
            </div>
          </div>
          <label class="personal-field">
            <span>昵称</span>
            <input v-model.trim="profileName" maxlength="16" placeholder="2-16 个字符" />
            <small>昵称将显示在顶栏与收益榜（脱敏展示）</small>
          </label>
          <div class="personal-modal__actions">
            <button class="personal-button personal-button--ghost" type="button" @click="closeProfile">取消</button>
            <button class="personal-button personal-button--primary" type="submit" :disabled="profileSubmitting">
              {{ profileSubmitting ? '保存中…' : '保存' }}
            </button>
          </div>
        </form>
      </div>
    </transition>

    <transition name="personal-modal-fade">
      <div
        v-if="passwordVisible"
        class="personal-modal"
        role="dialog"
        aria-modal="true"
        :aria-label="passwordCopy.dialogTitle"
      >
        <div class="personal-modal__backdrop" @click.stop />
        <form class="personal-modal__panel" @submit.prevent="savePassword">
          <div class="personal-modal__title">
            <h3>{{ passwordCopy.dialogTitle }}</h3>
            <button type="button" :aria-label="passwordCopy.close" @click="closePassword">×</button>
          </div>
          <label class="personal-field">
            <span>{{ passwordCopy.oldPassword }}</span>
            <span class="personal-password-field">
              <input
                v-model="passwordForm.oldPassword"
                :type="passwordFieldVisible.oldPassword ? 'text' : 'password'"
                autocomplete="current-password"
                :placeholder="passwordCopy.oldPlaceholder"
              />
              <button
                class="personal-password-field__toggle"
                :class="{ active: passwordFieldVisible.oldPassword }"
                type="button"
                :title="passwordFieldVisible.oldPassword ? passwordCopy.hideOld : passwordCopy.showOld"
                :aria-label="passwordFieldVisible.oldPassword ? passwordCopy.hideOld : passwordCopy.showOld"
                :aria-pressed="String(passwordFieldVisible.oldPassword)"
                @mousedown.prevent
                @click.prevent.stop="passwordFieldVisible.oldPassword = !passwordFieldVisible.oldPassword"
              >
                <PasswordVisibilityIcon :visible="passwordFieldVisible.oldPassword" />
              </button>
            </span>
          </label>
          <label class="personal-field">
            <span>{{ passwordCopy.newPassword }}</span>
            <span class="personal-password-field">
              <input
                v-model="passwordForm.newPassword"
                :type="passwordFieldVisible.newPassword ? 'text' : 'password'"
                autocomplete="new-password"
                maxlength="32"
                :aria-invalid="String(Boolean(passwordForm.newPassword) && !passwordFormatValid)"
                :placeholder="passwordCopy.newPlaceholder"
              />
              <button
                class="personal-password-field__toggle"
                :class="{ active: passwordFieldVisible.newPassword }"
                type="button"
                :title="passwordFieldVisible.newPassword ? passwordCopy.hideNew : passwordCopy.showNew"
                :aria-label="passwordFieldVisible.newPassword ? passwordCopy.hideNew : passwordCopy.showNew"
                :aria-pressed="String(passwordFieldVisible.newPassword)"
                @mousedown.prevent
                @click.prevent.stop="passwordFieldVisible.newPassword = !passwordFieldVisible.newPassword"
              >
                <PasswordVisibilityIcon :visible="passwordFieldVisible.newPassword" />
              </button>
            </span>
            <span
              v-if="passwordForm.newPassword"
              class="personal-password-strength"
              :class="`is-level-${passwordStrengthLevel}`"
              aria-live="polite"
            >
              <span class="personal-password-strength__bars" aria-hidden="true">
                <i
                  v-for="level in 3"
                  :key="level"
                  :class="{ active: level <= passwordStrengthLevel }"
                />
              </span>
              <em>{{ passwordCopy.strength }}：{{ passwordStrengthText }}</em>
            </span>
            <small
              v-if="passwordForm.newPassword && !passwordFormatValid"
              class="personal-password-error"
            >
              {{ passwordCopy.requirement }}
            </small>
          </label>
          <label class="personal-field">
            <span>{{ passwordCopy.confirmPassword }}</span>
            <span class="personal-password-field">
              <input
                v-model="passwordForm.confirmPassword"
                :type="passwordFieldVisible.confirmPassword ? 'text' : 'password'"
                autocomplete="new-password"
                maxlength="32"
                :aria-invalid="String(passwordConfirmMismatch)"
                :placeholder="passwordCopy.confirmPlaceholder"
              />
              <button
                class="personal-password-field__toggle"
                :class="{ active: passwordFieldVisible.confirmPassword }"
                type="button"
                :title="passwordFieldVisible.confirmPassword ? passwordCopy.hideConfirm : passwordCopy.showConfirm"
                :aria-label="passwordFieldVisible.confirmPassword ? passwordCopy.hideConfirm : passwordCopy.showConfirm"
                :aria-pressed="String(passwordFieldVisible.confirmPassword)"
                @mousedown.prevent
                @click.prevent.stop="passwordFieldVisible.confirmPassword = !passwordFieldVisible.confirmPassword"
              >
                <PasswordVisibilityIcon :visible="passwordFieldVisible.confirmPassword" />
              </button>
            </span>
            <small v-if="passwordConfirmMismatch" class="personal-password-error">
              {{ passwordCopy.mismatch }}
            </small>
          </label>
          <div class="personal-modal__warning">{{ passwordCopy.warning }}</div>
          <div class="personal-modal__actions">
            <button class="personal-button personal-button--ghost" type="button" @click="closePassword">
              {{ passwordCopy.cancel }}
            </button>
            <button class="personal-button personal-button--primary" type="submit" :disabled="passwordSubmitting">
              {{ passwordSubmitting ? passwordCopy.submitting : passwordCopy.confirm }}
            </button>
          </div>
        </form>
      </div>
    </transition>

    <transition name="personal-modal-fade">
      <div
        v-if="apiVisible"
        class="personal-modal"
        role="dialog"
        aria-modal="true"
        :aria-label="apiForm.rebinding ? '修改交易所 API' : '绑定交易所 API'"
      >
        <div class="personal-modal__backdrop" @click.stop />
        <form class="personal-modal__panel personal-modal__panel--api" @submit.prevent="requestBindApi">
          <div class="personal-modal__title">
            <h3>{{ apiForm.rebinding ? '修改交易所 API' : '绑定交易所 API' }}</h3>
            <button type="button" aria-label="关闭" @click="closeApi">×</button>
          </div>
          <div class="personal-api__binding-exchange">
            <span class="personal-api__binding-logo">
              <img v-if="currentApiExchange.logo" :src="currentApiExchange.logo" :alt="`${currentApiExchange.displayName || currentApiExchange.name} Logo`" />
            </span>
            <strong>正在绑定 {{ currentApiExchange.displayName || currentApiExchange.name }} 账户 API</strong>
            <button type="button" @click="closeApi">更换交易所请返回列表</button>
          </div>
          <label class="personal-field">
            <span>API Key</span>
            <input v-model.trim="apiForm.apiKey" autocomplete="off" placeholder="请粘贴 API Key" />
          </label>
          <label class="personal-field">
            <span>API Secret</span>
            <input v-model.trim="apiForm.secretKey" type="password" autocomplete="new-password" placeholder="请粘贴 API Secret" />
          </label>
          <label v-if="Number(apiForm.robotType) === 1" class="personal-field">
            <span>Passphrase</span>
            <input
              v-model="apiForm.passphrase"
              type="password"
              autocomplete="new-password"
              placeholder="OKX API 创建时设置的口令"
              @input="sanitizePassphrase"
            />
            <small>Passphrase 必须与 OKX 保持一致，不能包含中文、非英文字符或空格</small>
          </label>
          <label class="personal-field">
            <span>验证信息 <em>（{{ maskedVerificationAccount || '未绑定接收账号' }}）</em></span>
            <div class="personal-field__code">
              <input v-model.trim="apiForm.vcode" maxlength="6" inputmode="numeric" placeholder="请输入邮箱验证码" />
              <button type="button" :disabled="codeSeconds > 0 || codeSending" @click="sendApiCode">
                {{ codeSeconds > 0 ? `${codeSeconds}s 后重发` : (codeSending ? '发送中…' : '发送验证码') }}
              </button>
            </div>
          </label>
          <div class="personal-api__scope">
            <span>授权范围</span><b>只读</b><b>交易</b><b class="disabled">禁止提现</b>
          </div>
          <div class="personal-api__bind-notice">密钥经 AES-256 加密存储，仅用于查询与下单；请勿开启提现权限，建议同时设置 IP 白名单。</div>
          <div class="personal-api__tutorial-entry">
            <span>不知道如何创建 API？</span>
            <button type="button" @click="tutorialVisible = true">查看图文教程 <i>›</i></button>
          </div>
          <div class="personal-modal__actions">
            <button class="personal-button personal-button--ghost" type="button" @click="closeApi">取消</button>
            <button class="personal-button personal-button--primary" type="submit" :disabled="apiSubmitting || apiLoading">
              {{ apiSubmitting
                ? (apiForm.rebinding ? '修改中…' : '绑定中…')
                : (apiLoading ? '读取中…' : (apiForm.rebinding ? '确认修改' : '确认绑定')) }}
            </button>
          </div>
        </form>
      </div>
    </transition>

    <ApiBindingTutorialModal
      :visible="tutorialVisible"
      :robot-type="apiForm.robotType"
      @close="tutorialVisible = false"
    />

    <transition name="personal-modal-fade">
      <div v-if="confirmVisible" class="personal-modal" role="dialog" aria-modal="true" aria-label="确认操作">
        <div class="personal-modal__backdrop" @click.stop />
        <div :class="['personal-modal__panel', 'personal-confirm', { 'personal-confirm--bind-warning': confirmKind === 'bind-warning' }]">
          <template v-if="confirmKind === 'bind-warning'">
            <h3>重要提示</h3>
            <p class="bind-warning__message">{{ confirmDescription }}</p>
            <div v-if="warningRobotType !== 0" class="bind-warning__details">
              <p class="bind-warning__discount">专属链接注册限时享<strong>85折</strong>燃料费折扣</p>
              <div class="bind-warning__hint">
                <span>提示：</span>
                <span>1.新注册欧易用户无需实名认证即可开启机器人</span>
                <span>{{ warningExchangeName }}注册链接</span>
                <div class="bind-warning__link-row">
                  <span>{{ warningRegistryLink }}</span>
                  <button type="button" @click="copyApiRegistryLink">复制链接</button>
                </div>
              </div>
            </div>
            <div class="personal-modal__actions bind-warning__actions">
              <button class="personal-button personal-button--ghost" type="button" :disabled="apiSubmitting" @click="confirmOperation">
                {{ apiSubmitting ? '处理中…' : '继续绑定' }}
              </button>
              <button class="personal-button personal-button--primary" type="button" :disabled="apiSubmitting" @click="closeConfirm">取消</button>
            </div>
          </template>
          <template v-else>
            <span class="personal-confirm__icon"><i class="el-icon-warning-outline" /></span>
            <h3 v-if="confirmTitle">{{ confirmTitle }}</h3>
            <p>{{ confirmDescription }}</p>
            <div class="personal-modal__actions">
              <button class="personal-button personal-button--ghost" type="button" @click="closeConfirm">取消</button>
              <button
                :class="['personal-button', confirmKind === 'unbind' ? 'personal-button--confirm-danger' : 'personal-button--primary']"
                type="button"
                :disabled="apiSubmitting"
                @click="confirmOperation"
              >
                {{ apiSubmitting ? '处理中…' : confirmActionText }}
              </button>
            </div>
          </template>
        </div>
      </div>
    </transition>
  </section>
</template>

<script>
import {
  bindExchangeApi,
  changePassword,
  ClearApi,
  getAuthCode,
  getBtApiInfo,
  getGtApiInfo,
  getOkApiInfo,
  getUserApiInfo
} from '@/server/auth'
import { loadExchangeStrategies } from '@/api/portal'
import { editUserAvatar, editUserName } from '@/server/user'
import ApiBindingTutorialModal from '@/components/rebuild/ApiBindingTutorialModal.vue'
import PasswordVisibilityIcon from '@/components/rebuild/PasswordVisibilityIcon.vue'
import binanceLogo from '@/assets/exchanges/binance.png'
import okxLogo from '@/assets/exchanges/okx.png'
import gateLogo from '@/assets/exchanges/gate.png'
import bybitLogo from '@/assets/exchanges/bybit.png'
import { firstText, mapRobot, maskAccount } from '@/utils/portal-data'
import { refreshReferralLinks } from '@/utils/referral-links'
import registeredAddress from '@/utils/registered_address'

const STRONG_PASSWORD = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d\s])\S{8,32}$/
const PASSWORD_COPY = Object.freeze({
  'zh-CN': {
    accountSecurity: '账户安全',
    loginPassword: '登录密码',
    configured: '已设置',
    change: '修改',
    lastLogin: '最近登录',
    today: '今天',
    dialogTitle: '修改登录密码',
    close: '关闭',
    oldPassword: '原登录密码',
    oldPlaceholder: '请输入原登录密码',
    showOld: '显示原登录密码',
    hideOld: '隐藏原登录密码',
    newPassword: '新登录密码',
    newPlaceholder: '至少 8 位，含大小写字母、数字和符号',
    showNew: '显示新登录密码',
    hideNew: '隐藏新登录密码',
    confirmPassword: '确认新密码',
    confirmPlaceholder: '再次输入新密码',
    showConfirm: '显示确认密码',
    hideConfirm: '隐藏确认密码',
    strength: '密码安全性',
    weak: '弱',
    medium: '中',
    strong: '强',
    requirement: '需为 8-32 位，并同时包含大小写字母、数字和特殊字符',
    mismatch: '两次输入的新密码不一致',
    warning: '修改成功后需重新登录，且 24 小时内暂停提现，以保障资产安全。',
    cancel: '取消',
    confirm: '确定',
    submitting: '提交中…',
    enterOld: '请输入原登录密码',
    invalidNew: '新密码需为 8-32 位，包含大小写字母、数字和特殊字符',
    samePassword: '新密码不能与原密码相同',
    missingAccount: '未找到登录账号',
    success: '登录密码已修改，请重新登录',
    failure: '登录密码修改失败'
  },
  'zh-HK': {
    accountSecurity: '帳戶安全',
    loginPassword: '登入密碼',
    configured: '已設定',
    change: '修改',
    lastLogin: '最近登入',
    today: '今天',
    dialogTitle: '修改登入密碼',
    close: '關閉',
    oldPassword: '原登入密碼',
    oldPlaceholder: '請輸入原登入密碼',
    showOld: '顯示原登入密碼',
    hideOld: '隱藏原登入密碼',
    newPassword: '新登入密碼',
    newPlaceholder: '至少 8 位，包含大小寫英文字母、數字及符號',
    showNew: '顯示新登入密碼',
    hideNew: '隱藏新登入密碼',
    confirmPassword: '確認新密碼',
    confirmPlaceholder: '再次輸入新密碼',
    showConfirm: '顯示確認密碼',
    hideConfirm: '隱藏確認密碼',
    strength: '密碼安全性',
    weak: '弱',
    medium: '中',
    strong: '強',
    requirement: '需為 8-32 位，並同時包含大小寫英文字母、數字及特殊符號',
    mismatch: '兩次輸入的新密碼不一致',
    warning: '修改成功後需重新登入，且 24 小時內暫停提現，以保障資產安全。',
    cancel: '取消',
    confirm: '確定',
    submitting: '提交中…',
    enterOld: '請輸入原登入密碼',
    invalidNew: '新密碼需為 8-32 位，包含大小寫英文字母、數字及特殊符號',
    samePassword: '新密碼不能與原密碼相同',
    missingAccount: '找不到登入帳戶',
    success: '登入密碼已修改，請重新登入',
    failure: '登入密碼修改失敗'
  },
  en: {
    accountSecurity: 'Account security',
    loginPassword: 'Login password',
    configured: 'Set',
    change: 'Change',
    lastLogin: 'Last login',
    today: 'Today',
    dialogTitle: 'Change login password',
    close: 'Close',
    oldPassword: 'Current password',
    oldPlaceholder: 'Enter your current password',
    showOld: 'Show current password',
    hideOld: 'Hide current password',
    newPassword: 'New password',
    newPlaceholder: '8–32 characters with upper/lowercase letters, numbers and symbols',
    showNew: 'Show new password',
    hideNew: 'Hide new password',
    confirmPassword: 'Confirm new password',
    confirmPlaceholder: 'Enter the new password again',
    showConfirm: 'Show confirmation password',
    hideConfirm: 'Hide confirmation password',
    strength: 'Password strength',
    weak: 'Weak',
    medium: 'Medium',
    strong: 'Strong',
    requirement: 'Use 8–32 characters with uppercase and lowercase letters, numbers, and symbols',
    mismatch: 'The new passwords do not match',
    warning: 'After the change, you must sign in again and withdrawals will be suspended for 24 hours to protect your assets.',
    cancel: 'Cancel',
    confirm: 'Confirm',
    submitting: 'Submitting…',
    enterOld: 'Enter your current password',
    invalidNew: 'The new password must be 8–32 characters and include uppercase and lowercase letters, numbers, and symbols',
    samePassword: 'The new password cannot be the same as the current password',
    missingAccount: 'Login account not found',
    success: 'Login password changed. Please sign in again',
    failure: 'Failed to change the login password'
  },
  ko: {
    accountSecurity: '계정 보안',
    loginPassword: '로그인 비밀번호',
    configured: '설정됨',
    change: '변경',
    lastLogin: '최근 로그인',
    today: '오늘',
    dialogTitle: '로그인 비밀번호 변경',
    close: '닫기',
    oldPassword: '기존 로그인 비밀번호',
    oldPlaceholder: '기존 로그인 비밀번호를 입력하세요',
    showOld: '기존 로그인 비밀번호 표시',
    hideOld: '기존 로그인 비밀번호 숨기기',
    newPassword: '새 로그인 비밀번호',
    newPlaceholder: '대문자, 소문자, 숫자, 특수문자를 포함한 8~32자',
    showNew: '새 로그인 비밀번호 표시',
    hideNew: '새 로그인 비밀번호 숨기기',
    confirmPassword: '새 비밀번호 확인',
    confirmPlaceholder: '새 비밀번호를 다시 입력하세요',
    showConfirm: '비밀번호 확인 표시',
    hideConfirm: '비밀번호 확인 숨기기',
    strength: '비밀번호 안전도',
    weak: '약함',
    medium: '보통',
    strong: '강함',
    requirement: '대문자, 소문자, 숫자, 특수문자를 모두 포함한 8~32자로 입력하세요',
    mismatch: '새 비밀번호가 일치하지 않습니다',
    warning: '변경 후 다시 로그인해야 하며 자산 보호를 위해 24시간 동안 출금이 제한됩니다.',
    cancel: '취소',
    confirm: '확인',
    submitting: '제출 중…',
    enterOld: '기존 로그인 비밀번호를 입력하세요',
    invalidNew: '새 비밀번호는 대문자, 소문자, 숫자, 특수문자를 포함한 8~32자여야 합니다',
    samePassword: '새 비밀번호는 기존 비밀번호와 같을 수 없습니다',
    missingAccount: '로그인 계정을 찾을 수 없습니다',
    success: '로그인 비밀번호가 변경되었습니다. 다시 로그인해 주세요',
    failure: '로그인 비밀번호 변경에 실패했습니다'
  }
})
const EXCHANGE_NAMES = { 0: 'Binance', 1: 'OKX', 2: 'Gate', 3: 'Bybit' }
const EXCHANGE_API_TYPES = { 0: 1, 1: 2, 2: 3, 3: 4 }
const EXCHANGE_LOGOS = {
  0: binanceLogo,
  1: okxLogo,
  2: gateLogo,
  3: bybitLogo
}
const EXCHANGE_UNBOUND_DESCRIPTIONS = {
  0: '生态成熟，支持现货与合约策略',
  1: '合约品类齐全，需额外填写 Passphrase',
  2: '币种覆盖广，适合小市值网格策略',
  3: '合约撮合速度快，适合高频策略'
}

const UserAvatar = {
  name: 'UserAvatar',
  props: {
    src: { type: String, default: '' },
    initial: { type: String, default: 'U' }
  },
  data() {
    return { failed: false }
  },
  watch: {
    src() {
      this.failed = false
    }
  },
  render(h) {
    const content = this.src && !this.failed
      ? h('img', {
        attrs: { src: this.src, alt: '' },
        on: { error: () => { this.failed = true } }
      })
      : h('span', this.initial)
    return h('span', { class: 'user-avatar' }, [content])
  }
}

function isSuccess(result) {
  return Boolean(result && result.success !== false)
}

function isApiSuccess(result) {
  return Boolean(result && result.success)
}

function apiTypeForRobot(robotType) {
  return EXCHANGE_API_TYPES[Number(robotType)] || null
}

function responseAvatar(result) {
  const data = result && result.data
  if (typeof data === 'string') return data
  if (!data || typeof data !== 'object') return ''
  return firstText(data, ['user_face_head', 'user_face', 'url', 'path'])
}

function maskApiKey(value) {
  const text = String(value || '').trim()
  if (!text) return ''
  if (text.length <= 8) return `${text.slice(0, 2)} ···· ${text.slice(-2)}`
  return `${text.slice(0, 8)} · · · · · · · · ${text.slice(-4)}`
}

export default {
  name: 'PersonalCenterPanel',
  components: { UserAvatar, ApiBindingTutorialModal, PasswordVisibilityIcon },
  props: {
    userInfo: { type: Object, default: () => ({}) },
    exchangeRobots: { type: Array, default: () => [] },
    lastSync: { type: [Date, String, Number], default: null },
    language: { type: String, default: 'zh-CN' },
    embeddedMobile: { type: Boolean, default: false },
    mobileView: {
      type: String,
      default: 'api',
      validator: value => ['api', 'personal-info', 'password'].includes(value)
    }
  },
  data() {
    return {
      profileVisible: false,
      profileName: '',
      profileAvatarFile: null,
      profileAvatarPreview: '',
      profileSubmitting: false,
      passwordVisible: false,
      passwordSubmitting: false,
      passwordForm: { oldPassword: '', newPassword: '', confirmPassword: '' },
      passwordFieldVisible: { oldPassword: false, newPassword: false, confirmPassword: false },
      apiVisible: false,
      tutorialVisible: false,
      apiLoading: false,
      apiSubmitting: false,
      apiForm: {
        robotType: 0,
        apiKey: '',
        secretKey: '',
        passphrase: '',
        vcode: '',
        exchangeLocked: false,
        rebinding: false
      },
      codeSeconds: 0,
      codeSending: false,
      codeTimer: null,
      confirmVisible: false,
      confirmKind: '',
      confirmCard: null,
      referralLinkVersion: 0,
      referralLinkRequest: null,
      robotUsageCounts: {},
      robotUsageRequestId: 0
    }
  },
  computed: {
    passwordCopy() {
      return PASSWORD_COPY[this.language] || PASSWORD_COPY['zh-CN']
    },
    rawAccount() {
      return firstText(this.userInfo, ['username', 'email', 'mobile'])
    },
    displayName() {
      const name = firstText(this.userInfo, ['name', 'nickname'])
      if (name) return name
      return this.rawAccount ? maskAccount(this.rawAccount) : 'UPlus 用户'
    },
    avatarUrl() {
      return firstText(this.userInfo, ['user_face_head', 'user_face', 'avatar', 'head_img'])
    },
    avatarInitial() {
      const source = firstText(this.userInfo, ['name', 'nickname', 'username', 'email', 'mobile']) || 'U'
      const local = source.includes('@') ? source.split('@')[0] : source
      const match = local.trim().match(/[A-Za-z0-9\u3400-\u9fff]/)
      return (match ? match[0] : 'U').toUpperCase()
    },
    registerDate() {
      const date = firstText(this.userInfo, ['create_time', 'register_time', 'add_time'])
      return date ? date.slice(0, 10) : ''
    },
    maskedEmail() {
      const email = firstText(this.userInfo, ['username', 'email'])
      return email.includes('@') ? maskAccount(email) : ''
    },
    maskedMobile() {
      const mobile = firstText(this.userInfo, ['mobile', 'phone'])
      if (!mobile) return ''
      if (mobile.length < 7) return mobile
      return `${mobile.slice(0, 3)} **** ${mobile.slice(-4)}`
    },
    membershipTag() {
      const value = firstText(this.userInfo, ['vip_name', 'member_name', 'policy_name', 'level_name'])
      if (value) return value.toUpperCase().includes('PRO') ? 'PRO' : value
      const level = firstText(this.userInfo, ['vip_level'])
      return level && level !== '0' ? 'PRO' : ''
    },
    membershipLabel() {
      const value = firstText(this.userInfo, ['vip_name', 'member_name', 'policy_name', 'level_name'])
      if (value) return value
      const level = firstText(this.userInfo, ['vip_level'])
      return level && level !== '0' ? '专业版' : '--'
    },
    invitation() {
      return firstText(this.userInfo, ['invitation', 'invite_code', 'invitation_code'])
    },
    mobileAccountRows() {
      return [
        { label: '账户昵称', value: this.displayName || '--' },
        { label: '注册邮箱', value: this.maskedEmail || this.$t('暂未绑定') },
        { label: '邀请码', value: this.invitation || '--', accent: Boolean(this.invitation) },
        { label: '注册时间', value: this.registerDate || '--' },
        { label: '已绑定交易所', value: `${this.boundApiCards.length} / ${this.apiCards.length}`, accent: true },
        { label: '登录保护', value: this.$t('已开启'), accent: true }
      ]
    },
    formattedLastLogin() {
      const serverValue = firstText(this.userInfo, [
        'last_login_time',
        'lastLoginTime',
        'login_time'
      ])
      const localValue = typeof window !== 'undefined'
        ? window.localStorage.getItem('loginTime')
        : ''
      const source = String(serverValue || localValue || '').trim()
      if (!source) return ''

      const dateValue = /^\d+$/.test(source)
        ? Number(source)
        : source.replace(/-/g, '/')
      const value = new Date(dateValue)
      if (Number.isNaN(value.getTime())) return source

      const pad = number => String(number).padStart(2, '0')
      const now = new Date()
      const time = `${pad(value.getHours())}:${pad(value.getMinutes())}`
      const isToday = value.getFullYear() === now.getFullYear() &&
        value.getMonth() === now.getMonth() &&
        value.getDate() === now.getDate()
      return isToday
        ? `${this.passwordCopy.today} ${time}`
        : `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())} ${time}`
    },
    verificationAccount() {
      return firstText(this.userInfo, ['username', 'mobile'])
    },
    maskedVerificationAccount() {
      const value = String(this.verificationAccount || '').trim()
      if (!value) return ''
      if (value.includes('@')) {
        const [local, domain] = value.split('@')
        const suffix = local.length > 3 ? local.slice(-3) : local.slice(-1)
        return `${local.slice(0, 1)}***${suffix}@${domain}`
      }
      if (value.length > 7) return `${value.slice(0, 3)}***${value.slice(-4)}`
      return value
    },
    formattedLastSync() {
      if (!this.lastSync) return ''
      const value = this.lastSync instanceof Date ? this.lastSync : new Date(this.lastSync)
      if (Number.isNaN(value.getTime())) return ''
      const pad = number => String(number).padStart(2, '0')
      const now = new Date()
      const time = `${pad(value.getHours())}:${pad(value.getMinutes())}`
      const isToday = value.getFullYear() === now.getFullYear() &&
        value.getMonth() === now.getMonth() &&
        value.getDate() === now.getDate()
      return isToday
        ? `今天 ${time}`
        : `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())} ${time}`
    },
    passwordFormatValid() {
      return STRONG_PASSWORD.test(this.passwordForm.newPassword)
    },
    passwordStrengthLevel() {
      const password = this.passwordForm.newPassword
      if (!password) return 0
      const typeCount = [
        /[a-z]/.test(password),
        /[A-Z]/.test(password),
        /\d/.test(password),
        /[^A-Za-z\d\s]/.test(password)
      ].filter(Boolean).length
      const validLength = password.length >= 8 && password.length <= 32
      const noWhitespace = !/\s/.test(password)
      if (!validLength || !noWhitespace || typeCount <= 2) return 1
      if (typeCount === 3) return 2
      return 3
    },
    passwordStrengthText() {
      return [
        '--',
        this.passwordCopy.weak,
        this.passwordCopy.medium,
        this.passwordCopy.strong
      ][this.passwordStrengthLevel]
    },
    passwordConfirmMismatch() {
      return Boolean(
        this.passwordForm.confirmPassword &&
        this.passwordForm.confirmPassword !== this.passwordForm.newPassword
      )
    },
    apiCards() {
      return this.exchangeRobots.map((item, index) => {
        const robotType = Number(item && item.robot_type)
        const normalizedType = Number.isFinite(robotType) ? robotType : index
        const name = EXCHANGE_NAMES[normalizedType] || `交易所 ${normalizedType}`
        const boundAt = firstText(item, ['bind_api_time'])
        const apiKey = firstText(item, [
          'api_key',
          'apiKey',
          'binance_api_key',
          'okex_api_key',
          'okx_api_key',
          'gate_api_key',
          'bybit_api_key'
        ])
        const usageCount = Object.prototype.hasOwnProperty.call(this.robotUsageCounts, normalizedType)
          ? this.robotUsageCounts[normalizedType]
          : null
        const descriptionParts = []
        if (boundAt) descriptionParts.push(`绑定于 ${boundAt.slice(0, 10)}`)
        if (this.formattedLastSync) descriptionParts.push(`最近同步 ${this.formattedLastSync}`)
        descriptionParts.push(
          usageCount === null
            ? '机器人数量暂不可用'
            : (usageCount > 0 ? `${usageCount} 台机器人使用中` : '暂无机器人使用')
        )
        return {
          raw: item || {},
          robotType: normalizedType,
          apiType: apiTypeForRobot(normalizedType),
          name,
          displayName: normalizedType === 2 ? 'Gate' : name,
          logo: EXCHANGE_LOGOS[normalizedType] || '',
          unboundDescription: EXCHANGE_UNBOUND_DESCRIPTIONS[normalizedType] || `绑定后即可在 ${name} 启动机器人`,
          maskedApiKey: maskApiKey(apiKey),
          bound: Number(item && item.bind_api) === 1,
          state: Number(item && item.state),
          boundDescription: descriptionParts.join(' · ')
        }
      })
    },
    bindableApiCards() {
      return this.apiCards.filter(card => !card.bound)
    },
    boundApiCards() {
      return this.apiCards.filter(card => card.bound)
    },
    apiFormCards() {
      if (!this.apiForm.exchangeLocked) return this.bindableApiCards
      const selected = this.apiCards.find(card => card.robotType === Number(this.apiForm.robotType))
      return selected ? [selected] : []
    },
    currentApiExchange() {
      const robotType = Number(this.apiForm.robotType)
      const selected = this.apiCards.find(card => card.robotType === robotType)
      return selected || {
        robotType,
        name: robotType === 2 ? 'Gate' : (EXCHANGE_NAMES[robotType] || '交易所'),
        displayName: robotType === 2 ? 'Gate' : (EXCHANGE_NAMES[robotType] || '交易所'),
        logo: EXCHANGE_LOGOS[robotType] || ''
      }
    },
    warningRobotType() {
      return this.confirmCard ? Number(this.confirmCard.robotType) : Number(this.apiForm.robotType)
    },
    warningExchangeName() {
      return this.warningRobotType === 1
        ? 'Okx'
        : (EXCHANGE_NAMES[this.warningRobotType] || '')
    },
    warningRegistryLink() {
      const linkVersion = this.referralLinkVersion
      const addressLoaders = {
        0: registeredAddress.binance_address,
        1: registeredAddress.okx_address,
        2: registeredAddress.gate_address,
        3: registeredAddress.bybit_address
      }
      const loader = addressLoaders[this.warningRobotType]
      return linkVersion >= 0 && loader ? loader.call(registeredAddress) : ''
    },
    confirmTitle() {
      if (this.confirmKind === 'bind-warning') return '重要提示'
      if (this.confirmKind === 'bind-risk') return '注意'
      return ''
    },
    confirmDescription() {
      if (this.confirmKind === 'unbind') return '确定要解绑Api吗？'
      if (this.confirmKind === 'unbind-blocked' || this.confirmKind === 'rebind-blocked') {
        return '请先前往“机器人”页面关闭机器人，再操作解绑Api！'
      }
      if (this.confirmKind === 'bind-warning') {
        const robotType = this.confirmCard ? Number(this.confirmCard.robotType) : Number(this.apiForm.robotType)
        return robotType === 0
          ? '您不符合Uplus专属用户规则，无法享受8.5折燃料费折扣,确定继续绑定吗?'
          : '您不是使用UPlus专属注册链接注册将无法使用优惠,确定继续绑定吗?'
      }
      return '请务必妥善保管您的 API 信息，不要向他人透露。如长期未发生交易，API 将于 15 天后自动删除。'
    },
    confirmActionText() {
      return this.confirmKind === 'bind-warning' ? '继续绑定' : '确定'
    }
  },
  mounted() {
    this.referralLinkRequest = this.refreshRegistryLinks()
  },
  watch: {
    exchangeRobots: {
      immediate: true,
      handler() {
        this.loadRobotUsageCounts()
      }
    }
  },
  beforeDestroy() {
    this.clearCodeTimer()
    this.releaseAvatarPreview()
  },
  methods: {
    toast(message, type = 'info') {
      this.$emit('toast', { message, type })
    },
    openProfile() {
      this.profileName = firstText(this.userInfo, ['name', 'nickname']) || ''
      this.profileAvatarFile = null
      this.releaseAvatarPreview()
      this.profileVisible = true
    },
    closeProfile() {
      if (this.profileSubmitting) return
      this.profileVisible = false
      this.profileAvatarFile = null
      this.releaseAvatarPreview()
    },
    selectAvatar(event) {
      const file = event && event.target && event.target.files && event.target.files[0]
      if (!file) return
      if (!['image/jpeg', 'image/png'].includes(file.type)) {
        this.toast('头像仅支持 JPG 或 PNG 图片', 'error')
        event.target.value = ''
        return
      }
      if (file.size > 4 * 1024 * 1024) {
        this.toast('头像图片不能超过 4MB', 'error')
        event.target.value = ''
        return
      }
      this.profileAvatarFile = file
      this.releaseAvatarPreview()
      this.profileAvatarPreview = URL.createObjectURL(file)
    },
    releaseAvatarPreview() {
      if (this.profileAvatarPreview) URL.revokeObjectURL(this.profileAvatarPreview)
      this.profileAvatarPreview = ''
    },
    async saveProfile() {
      const name = this.profileName.trim()
      const oldName = firstText(this.userInfo, ['name', 'nickname'])
      if (name && (name.length < 2 || name.length > 16)) {
        this.toast('昵称需为 2-16 个字符', 'error')
        return
      }
      if (!name && oldName) {
        this.toast('昵称不能为空', 'error')
        return
      }
      if (!this.profileAvatarFile && name === oldName) {
        this.closeProfile()
        return
      }

      this.profileSubmitting = true
      const updated = { ...this.userInfo }
      try {
        if (name !== oldName) {
          const nameResult = await editUserName(name)
          if (!isSuccess(nameResult)) throw new Error((nameResult && nameResult.message) || '昵称保存失败')
          updated.name = name
          updated.nickname = name
        }
        if (this.profileAvatarFile) {
          const form = new FormData()
          form.append('user_face', this.profileAvatarFile, `${Date.now()}-${this.profileAvatarFile.name}`)
          const avatarResult = await editUserAvatar(form)
          if (!isSuccess(avatarResult)) throw new Error((avatarResult && avatarResult.message) || '头像保存失败')
          const avatar = responseAvatar(avatarResult)
          if (avatar) {
            updated.user_face = avatar
            updated.user_face_head = avatar
          }
        }
        this.$emit('user-updated', updated)
        this.toast('资料已保存', 'success')
        this.profileVisible = false
        this.profileAvatarFile = null
        this.releaseAvatarPreview()
      } catch (error) {
        this.toast(error.message || '资料保存失败', 'error')
      } finally {
        this.profileSubmitting = false
      }
    },
    openPassword() {
      this.passwordForm = { oldPassword: '', newPassword: '', confirmPassword: '' }
      this.passwordFieldVisible = { oldPassword: false, newPassword: false, confirmPassword: false }
      this.passwordVisible = true
    },
    closePassword() {
      if (this.passwordSubmitting) return
      this.passwordVisible = false
      this.passwordFieldVisible = { oldPassword: false, newPassword: false, confirmPassword: false }
    },
    async savePassword() {
      const form = this.passwordForm
      if (!form.oldPassword) return this.toast(this.passwordCopy.enterOld, 'error')
      if (!STRONG_PASSWORD.test(form.newPassword)) {
        return this.toast(this.passwordCopy.invalidNew, 'error')
      }
      if (form.newPassword === form.oldPassword) return this.toast(this.passwordCopy.samePassword, 'error')
      if (form.newPassword !== form.confirmPassword) return this.toast(this.passwordCopy.mismatch, 'error')
      const email = firstText(this.userInfo, ['username'])
      const mobile = firstText(this.userInfo, ['mobile'])
      const account = email || mobile
      if (!account) return this.toast(this.passwordCopy.missingAccount, 'error')

      this.passwordSubmitting = true
      try {
        const result = await changePassword(email ? 1 : 2, account, form.oldPassword, form.newPassword)
        if (!isSuccess(result)) throw new Error((result && result.message) || this.passwordCopy.failure)
        this.passwordVisible = false
        this.toast(this.passwordCopy.success, 'success')
        this.$emit('password-changed')
      } catch (error) {
        this.toast(error.message || this.passwordCopy.failure, 'error')
      } finally {
        this.passwordSubmitting = false
      }
    },
    async copyInvitation() {
      try {
        await navigator.clipboard.writeText(this.invitation)
        this.toast('邀请码已复制', 'success')
      } catch (error) {
        this.toast('复制失败，请手动复制邀请码', 'error')
      }
    },
    async refreshRegistryLinks() {
      await refreshReferralLinks()
      this.referralLinkVersion += 1
    },
    async loadRobotUsageCounts() {
      const robotTypes = [...new Set(
        this.exchangeRobots
          .filter(item => Number(item && item.bind_api) === 1)
          .map(item => Number(item && item.robot_type))
          .filter(Number.isFinite)
      )]
      const requestId = ++this.robotUsageRequestId

      if (!robotTypes.length) {
        this.robotUsageCounts = {}
        return
      }

      const results = await Promise.all(
        robotTypes.map(robotType => loadExchangeStrategies(robotType))
      )
      if (requestId !== this.robotUsageRequestId) return

      this.robotUsageCounts = results.reduce((counts, result) => {
        if (result.strategyErrors && result.strategyErrors.length) {
          counts[result.robotType] = null
          return counts
        }
        const strategies = Array.isArray(result.strategies) ? result.strategies : []
        counts[result.robotType] = strategies
          .map((item, index) => mapRobot(item, index, strategies.length))
          .filter(robot => robot.status === 'running')
          .length
        return counts
      }, {})
    },
    async copyApiRegistryLink() {
      if (this.referralLinkRequest) await this.referralLinkRequest
      const link = this.warningRegistryLink
      if (!link) return
      try {
        if (!navigator.clipboard || !navigator.clipboard.writeText) throw new Error('CLIPBOARD_UNAVAILABLE')
        await navigator.clipboard.writeText(link)
        this.toast('注册地址复制成功，请到浏览器打开。', 'success')
      } catch (error) {
        const input = document.createElement('input')
        input.value = link
        document.body.appendChild(input)
        input.select()
        const copied = document.execCommand('copy')
        document.body.removeChild(input)
        this.toast(copied ? '注册地址复制成功，请到浏览器打开。' : '复制失败', copied ? 'success' : 'error')
      }
    },
    openNewApi() {
      const card = this.bindableApiCards[0]
      if (!card) {
        this.toast('当前交易所 API 均已绑定', 'info')
        return
      }
      this.openApi(card, false, false)
    },
    openApi(card, loadExisting = false, exchangeLocked = true) {
      if (loadExisting && card.bound && card.state === 1) {
        this.confirmKind = 'rebind-blocked'
        this.confirmCard = card
        this.confirmVisible = true
        return
      }
      this.apiForm = {
        robotType: card.robotType,
        apiKey: '',
        secretKey: '',
        passphrase: '',
        vcode: '',
        exchangeLocked,
        rebinding: Boolean(loadExisting && card.bound)
      }
      this.apiVisible = true
      if (loadExisting) this.loadApiCredentials(card.robotType)
    },
    closeApi() {
      if (this.apiSubmitting) return
      this.tutorialVisible = false
      this.apiVisible = false
      this.clearCodeTimer()
    },
    handleApiExchangeChange() {
      this.apiForm.apiKey = ''
      this.apiForm.secretKey = ''
      this.apiForm.passphrase = ''
      this.apiForm.vcode = ''
    },
    sanitizePassphrase(event) {
      const value = String((event && event.target && event.target.value) || this.apiForm.passphrase || '')
      const hasChinese = /[\u4E00-\u9FA5]/.test(value)
      const hasNonAscii = /[^\x00-\xff]/.test(value)
      const sanitized = value.replace(/[^\x00-\xff]/g, '').replace(/\s/g, '')
      if (value !== sanitized) {
        if (hasChinese) this.toast('密码短语不可包含汉字', 'error')
        else if (hasNonAscii) this.toast('密码短语不可包含特殊符号', 'error')
      }
      this.$nextTick(() => {
        this.apiForm.passphrase = sanitized
      })
    },
    async loadApiCredentials(robotType) {
      const loaders = [getUserApiInfo, getOkApiInfo, getGtApiInfo, getBtApiInfo]
      const loader = loaders[Number(robotType)]
      if (!loader) return
      this.apiLoading = true
      try {
        const result = await loader()
        if (!isApiSuccess(result)) throw new Error((result && result.message) || 'API 信息读取失败')
        const data = result.data && typeof result.data === 'object' ? result.data : {}
        this.apiForm.apiKey = firstText(data, ['binance_api_key', 'okex_api_key', 'gate_api_key', 'bybit_api_key'])
        this.apiForm.secretKey = firstText(data, ['binance_api_secret', 'okex_api_secret', 'gate_api_secret', 'bybit_api_secret'])
        this.apiForm.passphrase = firstText(data, ['okex_passphrase'])
      } catch (error) {
        this.toast(error.message || 'API 信息读取失败', 'error')
      } finally {
        this.apiLoading = false
      }
    },
    async sendApiCode() {
      if (this.codeSeconds > 0 || this.codeSending) return
      const email = firstText(this.userInfo, ['username'])
      const mobile = firstText(this.userInfo, ['mobile'])
      const address = email || mobile
      if (!address) return this.toast('您还没有绑定邮箱或者手机号，请先去绑定邮箱或手机号', 'error')
      this.codeSending = true
      try {
        const result = await getAuthCode(3, email ? 1 : 2, address)
        if (!isApiSuccess(result)) throw new Error((result && result.message) || '验证码发送失败')
        this.startCodeTimer()
        this.toast(`验证码已发送到您的${email ? '邮箱' : '手机'}请注意查收`, 'success')
      } catch (error) {
        this.toast(error.message || '验证码发送失败', 'error')
      } finally {
        this.codeSending = false
      }
    },
    startCodeTimer() {
      this.clearCodeTimer()
      this.codeSeconds = 60
      this.codeTimer = setInterval(() => {
        this.codeSeconds -= 1
        if (this.codeSeconds <= 0) this.clearCodeTimer()
      }, 1000)
    },
    clearCodeTimer() {
      if (this.codeTimer) clearInterval(this.codeTimer)
      this.codeTimer = null
      this.codeSeconds = 0
    },
    requestBindApi() {
      if (!this.apiForm.apiKey) return this.toast('请输入Api Key', 'error')
      if (!this.apiForm.secretKey) return this.toast('请输入Api Secret', 'error')
      if (Number(this.apiForm.robotType) === 1 && !this.apiForm.passphrase) {
        return this.toast('请输入密码短语', 'error')
      }
      if (!this.apiForm.vcode) return this.toast('请输入验证码', 'error')
      const card = this.apiCards.find(item => item.robotType === Number(this.apiForm.robotType)) || null
      if (!card || !card.apiType) return this.toast('未找到对应交易所', 'error')
      if (!this.apiForm.exchangeLocked && card.bound) {
        return this.toast(`${card.name} API 已绑定，请使用重新绑定`, 'error')
      }
      this.confirmKind = 'bind-risk'
      this.confirmCard = card
      this.confirmVisible = true
    },
    requestUnbind(card) {
      if (card.state === 1) {
        this.confirmKind = 'unbind-blocked'
        this.confirmCard = card
        this.confirmVisible = true
        return
      }
      this.confirmKind = 'unbind'
      this.confirmCard = card
      this.confirmVisible = true
    },
    closeConfirm() {
      if (this.apiSubmitting) return
      this.confirmVisible = false
      this.confirmKind = ''
      this.confirmCard = null
    },
    confirmOperation() {
      if (this.confirmKind === 'unbind-blocked' || this.confirmKind === 'rebind-blocked') {
        this.closeConfirm()
        return
      }
      if (this.confirmKind === 'unbind') {
        this.unbindApi()
        return
      }
      this.bindApi(this.confirmKind === 'bind-warning' ? 1 : 0)
    },
    async bindApi(ifConfirm = 0) {
      const email = firstText(this.userInfo, ['username'])
      const boundCard = this.confirmCard
      const wasBound = Boolean(boundCard && boundCard.bound)
      const apiType = boundCard && boundCard.apiType
        ? boundCard.apiType
        : apiTypeForRobot(this.apiForm.robotType)
      if (!apiType) return this.toast('未找到对应交易所', 'error')
      const params = {
        apiKey: this.apiForm.apiKey.trim(),
        secretKey: this.apiForm.secretKey.trim(),
        vcode: this.apiForm.vcode.trim(),
        apiType,
        vcodeType: email ? 1 : 2
      }
      if (Number(this.apiForm.robotType) === 1) params.okexPassphrase = this.apiForm.passphrase.trim()
      if (ifConfirm) params.ifConfirm = 1

      this.apiSubmitting = true
      try {
        const result = await bindExchangeApi(params)
        if (isApiSuccess(result)) {
          this.confirmVisible = false
          this.apiVisible = false
          this.clearCodeTimer()
          this.confirmKind = ''
          this.confirmCard = null
          if (wasBound && boundCard.state === 1) {
            this.toast('修改Api成功后，需手动重启机器人', 'success')
          } else {
            this.toast(wasBound ? '修改成功' : '绑定成功', 'success')
          }
          this.$emit('refresh-exchanges')
          return
        }

        if (result && result.message === 'warning') {
          if (this.referralLinkRequest) await this.referralLinkRequest
          this.confirmKind = 'bind-warning'
          return
        }

        throw new Error((result && result.message) || 'API 绑定失败')
      } catch (error) {
        this.toast(error.message || 'API 绑定失败', 'error')
      } finally {
        this.apiSubmitting = false
      }
    },
    async unbindApi() {
      const card = this.confirmCard
      if (!card) return
      this.apiSubmitting = true
      try {
        const result = await ClearApi(card.apiType)
        if (!isApiSuccess(result)) throw new Error((result && result.message) || 'API 解绑失败')
        this.confirmVisible = false
        this.confirmKind = ''
        this.confirmCard = null
        this.toast('解绑成功', 'success')
        this.$emit('refresh-exchanges')
      } catch (error) {
        this.toast(error.message || 'API 解绑失败', 'error')
      } finally {
        this.apiSubmitting = false
      }
    }
  }
}
</script>

<style scoped>
.personal-center {
  color: #1b1f2a;
}

.personal-center__heading {
  margin-bottom: 20px;
}

.personal-center__heading h1 {
  margin: 0;
  font-size: 24px;
  font-weight: 800;
  letter-spacing: .2px;
}

.personal-center__heading p,
.personal-api__heading p {
  margin: 5px 0 0;
  color: #8a90a0;
  font-size: 13px;
}

.personal-center__layout {
  display: grid;
  align-items: flex-start;
  grid-template-columns: 420px minmax(0, 1fr);
  gap: 20px;
}

.personal-center__side {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.personal-card {
  padding: 22px;
  border: 1px solid #eceef4;
  border-radius: 14px;
  background: #fff;
}

.personal-profile__summary {
  display: flex;
  align-items: center;
  gap: 14px;
}

::v-deep .user-avatar {
  display: flex;
  overflow: hidden;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  color: #1f6bff;
  border-radius: 50%;
  background: #e4eefe;
  font-weight: 800;
}

::v-deep .user-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.personal-profile__avatar {
  width: 60px;
  height: 60px;
  font-size: 25px;
}

.personal-profile__identity {
  min-width: 0;
  flex: 1;
}

.personal-profile__identity > div {
  display: flex;
  align-items: center;
  gap: 8px;
}

.personal-profile__identity strong {
  overflow: hidden;
  font-size: 17px;
  font-weight: 800;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.personal-profile__identity small {
  display: block;
  overflow: hidden;
  margin-top: 4px;
  color: #98a0af;
  font-size: 12.5px;
  font-variant-numeric: tabular-nums;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.personal-profile__pro {
  padding: 2px 9px;
  color: #7a5200;
  border-radius: 999px;
  background: linear-gradient(135deg, #f9e7b3, #efc65c);
  font-size: 10px;
  font-weight: 800;
  letter-spacing: .8px;
  white-space: nowrap;
}

.personal-button {
  min-height: 34px;
  padding: 7px 13px;
  border: 0;
  border-radius: 8px;
  font-size: 12.5px;
  font-weight: 700;
  white-space: nowrap;
}

.personal-button:disabled {
  cursor: not-allowed;
  opacity: .55;
}

.personal-button--ghost {
  color: #3a3f4e;
  border: 1px solid #dcdfe8;
  background: #fff;
}

.personal-button--ghost:hover:not(:disabled) {
  color: #1f6bff;
  border-color: #a9c4f7;
}

.personal-button--primary,
.personal-button--confirm-danger {
  color: #fff;
  background: #1f6bff;
}

.personal-button--primary:hover:not(:disabled) {
  background: #1558db;
}

.personal-button--danger {
  color: #d92d20;
  background: #fceae8;
}

.personal-button--soft {
  padding-right: 18px;
  padding-left: 18px;
  color: #1f6bff;
  background: #ebf1fe;
}

.personal-button--confirm-danger {
  background: #e5484d;
}

.personal-profile__details {
  margin: 16px 0 0;
}

.personal-profile__details > div {
  display: flex;
  min-height: 48px;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  border-top: 1px solid #f3f4f8;
}

.personal-profile__details dt {
  color: #79808f;
  font-size: 13px;
}

.personal-profile__details dd {
  overflow: hidden;
  margin: 0;
  font-size: 13.5px;
  font-weight: 600;
  text-align: right;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.personal-profile__details dd.is-member {
  color: #b7791f;
  font-weight: 700;
}

.personal-profile__invitation {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.personal-profile__invitation b {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 13.5px;
  font-weight: 800;
}

.personal-profile__invitation button {
  padding: 0;
  color: #1f6bff;
  border: 0;
  background: none;
  font-size: 12.5px;
  font-weight: 700;
}

.personal-security h2,
.personal-api h2 {
  margin: 0;
  font-size: 16px;
  font-weight: 800;
}

.personal-security__item {
  display: flex;
  min-height: 66px;
  align-items: center;
  justify-content: space-between;
  margin-top: 6px;
  gap: 16px;
  border-top: 1px solid #f3f4f8;
}

.personal-security__item strong,
.personal-security__item small {
  display: block;
}

.personal-security__item strong {
  font-size: 13.5px;
}

.personal-security__item small {
  margin-top: 2px;
  color: #98a0af;
  font-size: 12px;
}

.personal-security__last-login {
  padding-top: 12px;
  color: #98a0af;
  border-top: 1px solid #f3f4f8;
  font-size: 12px;
  line-height: 1.5;
}

.personal-api__heading {
  display: block;
}

.personal-api__heading > div {
  min-width: 0;
}

.personal-api__support {
  display: flex;
  min-height: 52px;
  padding: 8px 14px;
  margin-top: 20px;
  align-items: center;
  gap: 12px;
  border-radius: 10px;
  background: #f7f8fb;
}

.personal-api__support > strong {
  flex: 0 0 auto;
  color: #5a6072;
  font-size: 13px;
  font-weight: 700;
}

.personal-api__support-list {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.personal-api__support-item {
  display: inline-flex;
  height: 32px;
  padding: 0 10px 0 5px;
  align-items: center;
  gap: 7px;
  color: #2b3040;
  border: 1px solid #e3e7ef;
  border-radius: 999px;
  background: #fff;
  white-space: nowrap;
}

.personal-api__support-item.is-bound {
  border-color: #a9dec1;
}

.personal-api__support-item img {
  display: block;
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.personal-api__support-item b {
  font-size: 13px;
  font-weight: 700;
}

.personal-api__support-item i {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #d7dce7;
}

.personal-api__support-item.is-bound i {
  background: #12b76a;
}

.personal-api__bound-count {
  margin-left: auto;
  color: #1f6bff;
  font-size: 13px;
  font-weight: 800;
  white-space: nowrap;
}

.personal-api__bound-list {
  display: flex;
  margin-top: 16px;
  flex-direction: column;
  gap: 12px;
}

.personal-api__card {
  padding: 18px;
  border: 1px solid #e4e8f0;
  border-radius: 12px;
  background: #fff;
}

.personal-api__card-main {
  display: flex;
  align-items: center;
  gap: 12px;
}

.personal-api__exchange-logo {
  display: flex;
  width: 42px;
  height: 42px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 11px;
  background: #f4f6f9;
}

.personal-api__exchange-logo.is-exchange-0 {
  background: #fff8e6;
}

.personal-api__exchange-logo.is-exchange-1 {
  background: #f3f4f6;
}

.personal-api__exchange-logo.is-exchange-2 {
  background: #edf6ff;
}

.personal-api__exchange-logo.is-exchange-3 {
  background: #f1f2f4;
}

.personal-api__exchange-logo img {
  display: block;
  width: 30px;
  height: 30px;
  object-fit: contain;
}

.personal-api__exchange-copy {
  min-width: 0;
  flex: 1;
}

.personal-api__exchange-copy > div {
  display: flex;
  align-items: center;
  gap: 8px;
}

.personal-api__exchange-copy strong {
  color: #111827;
  font-size: 15.5px;
  font-weight: 800;
}

.personal-api__exchange-copy small {
  display: block;
  overflow: hidden;
  margin-top: 3px;
  color: #98a0af;
  font-size: 12.5px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.personal-api__bound {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 9px;
  color: #0b7a47;
  border-radius: 999px;
  background: #e8f6ee;
  font-size: 11.5px;
  font-weight: 700;
}

.personal-api__bound i {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #12b76a;
}

.personal-api__actions {
  display: flex;
  flex: 0 0 auto;
  gap: 8px;
}

.personal-api__permissions {
  display: flex;
  min-height: 48px;
  align-items: center;
  gap: 10px;
  padding: 9px 14px;
  margin-top: 16px;
  border-radius: 9px;
  background: #f7f8fb;
}

.personal-api__key {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 12px;
}

.personal-api__key em {
  color: #79808f;
  font-size: 12.5px;
  font-style: normal;
}

.personal-api__key > b {
  overflow: hidden;
  color: #162033;
  font-family: Consolas, Monaco, monospace;
  font-size: 12.5px;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.personal-api__permissions > div {
  display: flex;
  margin-left: auto;
  gap: 6px;
}

.personal-api__permissions > div b,
.personal-api__scope b {
  padding: 3px 10px;
  color: #1f6bff;
  border-radius: 999px;
  background: #e4eefe;
  font-size: 11.5px;
  font-weight: 700;
}

.personal-api__permissions > div b.disabled,
.personal-api__scope b.disabled {
  color: #98a0af;
  background: #f1f2f6;
}

.personal-api__unbound-grid {
  display: grid;
  margin-top: 14px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
}

.personal-api__unbound-card {
  min-width: 0;
  min-height: 142px;
  padding: 17px 18px;
  border: 1px dashed #d7ddea;
  border-radius: 12px;
  background: #fff;
}

.personal-api__unbound-heading {
  display: flex;
  align-items: center;
  gap: 10px;
}

.personal-api__unbound-heading > strong {
  min-width: 0;
  overflow: hidden;
  color: #111827;
  font-size: 15px;
  font-weight: 800;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.personal-api__unbound-heading > span:last-child {
  padding: 3px 9px;
  margin-left: auto;
  color: #98a0af;
  border-radius: 999px;
  background: #f1f2f6;
  font-size: 11.5px;
  font-weight: 700;
  white-space: nowrap;
}

.personal-api__unbound-card p {
  overflow: hidden;
  margin: 11px 0 0;
  color: #98a0af;
  font-size: 12.5px;
  line-height: 20px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.personal-api__unbound-card > button {
  padding: 0;
  margin-top: 13px;
  color: #1f6bff;
  border: 0;
  outline: none;
  background: transparent;
  font-size: 13.5px;
  font-weight: 700;
  cursor: pointer;
}

.personal-api__unbound-card > button:hover {
  color: #1557d5;
}

.personal-api__unbound-card > button i {
  font-size: 17px;
  font-style: normal;
  vertical-align: -1px;
}

.personal-api__notice,
.personal-api__bind-notice {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  padding: 12px 16px;
  margin-top: 16px;
  color: #4b71b8;
  border-radius: 10px;
  background: #f3f7fe;
  font-size: 12.5px;
  line-height: 1.7;
}

.personal-api__notice i {
  margin-top: 2px;
  color: #1f6bff;
  font-size: 16px;
}

.personal-api__empty {
  display: grid;
  min-height: 180px;
  place-items: center;
  margin-top: 14px;
  color: #98a0af;
  border: 1.5px dashed #d5d9e4;
  border-radius: 12px;
  font-size: 13px;
}

.personal-modal {
  position: fixed;
  z-index: 3000;
  inset: 0;
  display: grid;
  place-items: center;
  padding: 24px;
}

.personal-modal__backdrop {
  position: absolute;
  inset: 0;
  background: rgba(16, 24, 40, .48);
}

.personal-modal__panel {
  position: relative;
  z-index: 1;
  width: min(100%, 480px);
  max-height: calc(100vh - 48px);
  overflow-y: auto;
  padding: 24px;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 24px 80px rgba(16, 24, 40, .24);
}

.personal-modal__panel--api {
  width: min(100%, 512px);
  padding: 20px;
}

.personal-modal__title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
}

.personal-modal__title h3,
.personal-confirm h3 {
  margin: 0;
  font-size: 19px;
  font-weight: 700;
}

.personal-modal__title button {
  width: 30px;
  height: 30px;
  padding: 0;
  color: #8a90a0;
  border: 0;
  border-radius: 8px;
  background: transparent;
  font-size: 22px;
  line-height: 1;
}

.personal-modal__title button:hover {
  background: #f5f6f9;
}

.personal-field,
.personal-field__label {
  display: block;
  margin-bottom: 13px;
  color: #5a6072;
  font-size: 13.5px;
  font-weight: 600;
}

.personal-field > span {
  display: block;
  margin-bottom: 7px;
}

.personal-field > span em {
  color: #99a2b3;
  font-style: normal;
  font-weight: 500;
}

.personal-field input,
.personal-field select {
  width: 100%;
  height: 42px;
  padding: 0 13px;
  outline: none;
  color: #1b1f2a;
  border: 1px solid #dfe3ec;
  border-radius: 8px;
  background: #fafbfd;
  font-size: 14px;
}

.personal-modal__panel .personal-field input::placeholder {
  color: #b8bfcc;
  font-weight: 400;
  opacity: 1;
}

.personal-password-field {
  position: relative;
  display: block;
  margin-bottom: 0 !important;
}

.personal-password-field input {
  padding-right: 46px;
}

.personal-password-field__toggle {
  position: absolute;
  top: 50%;
  right: 9px;
  display: inline-flex;
  width: 30px;
  height: 30px;
  padding: 0;
  align-items: center;
  justify-content: center;
  color: #98a0af;
  border: 0;
  border-radius: 7px;
  outline: none;
  background: transparent;
  cursor: pointer;
  transform: translateY(-50%);
  transition: color .16s ease, background-color .16s ease;
}

.personal-password-field__toggle:hover {
  color: #5a6072;
  background: #eef2f8;
}

.personal-password-field__toggle.active {
  color: #1f6bff;
  background: #ebf1fe;
}

.personal-password-field__toggle:focus-visible {
  color: #1f6bff;
  box-shadow: 0 0 0 2px rgba(31, 107, 255, .18);
}

.personal-password-strength {
  display: flex !important;
  align-items: center;
  margin: 8px 0 0 !important;
  gap: 10px;
}

.personal-password-strength__bars {
  display: grid !important;
  min-width: 0;
  flex: 1;
  grid-template-columns: repeat(3, 1fr);
  margin: 0 !important;
  gap: 6px;
}

.personal-password-strength__bars i {
  height: 4px;
  border-radius: 999px;
  background: #e7eaf0;
  transition: background-color .16s ease;
}

.personal-password-strength.is-level-1 i.active {
  background: #e5484d;
}

.personal-password-strength.is-level-2 i.active {
  background: #e8a033;
}

.personal-password-strength.is-level-3 i.active {
  background: #10a36d;
}

.personal-password-strength em {
  min-width: 78px;
  color: #79808f;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  text-align: right;
}

.personal-field small.personal-password-error {
  margin-top: 6px;
  color: #e5484d;
  line-height: 1.45;
}

.personal-password-field input[aria-invalid='true'] {
  border-color: #e9a3a6;
}

.personal-field input:focus,
.personal-field select:focus {
  border-color: #1f6bff;
  box-shadow: 0 0 0 3px rgba(31, 107, 255, .12);
}

.personal-field small {
  display: block;
  margin-top: 6px;
  color: #98a0af;
  font-size: 12px;
  font-weight: 400;
}

.profile-editor__avatar-row {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 15px;
}

.profile-editor__avatar {
  width: 56px;
  height: 56px;
  font-size: 23px;
}

.profile-editor__file {
  display: none;
}

.profile-editor__avatar-row p {
  margin: 7px 0 0;
  color: #98a0af;
  font-size: 12px;
}

.personal-modal__warning {
  padding: 11px 13px;
  margin-bottom: 18px;
  color: #91600a;
  border-radius: 8px;
  background: #fbf1df;
  font-size: 12.5px;
  line-height: 1.6;
}

.personal-modal__actions {
  display: flex;
  gap: 10px;
}

.personal-modal__actions .personal-button {
  min-height: 42px;
  flex: 1;
  font-size: 14px;
}

.personal-field__code {
  position: relative;
  display: block;
}

.personal-field__code button {
  position: absolute;
  top: 1px;
  right: 1px;
  min-width: 104px;
  height: 40px;
  padding: 0 12px;
  color: #1f6bff;
  border: 0;
  border-radius: 0 7px 7px 0;
  background: #fafbfd;
  font-size: 12.5px;
  font-weight: 700;
}

.personal-field__code input {
  padding-right: 112px;
}

.personal-field__code button:disabled {
  color: #a2a9b7;
}

.personal-api__scope {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 2px 0 13px;
}

.personal-api__scope > span {
  margin-right: 2px;
  color: #5a6072;
  font-size: 13px;
  font-weight: 600;
}

.personal-api__bind-notice {
  padding: 11px 13px;
  margin: 0 0 11px;
  border-radius: 8px;
  background: #f2f6fd;
  font-size: 12px;
  line-height: 1.55;
}

.personal-api__tutorial-entry {
  display: flex;
  margin: 0 0 17px;
  align-items: center;
  color: #8d95a6;
  font-size: 12px;
  font-weight: 400;
}

.personal-api__tutorial-entry button {
  display: inline-flex;
  padding: 0;
  margin-left: 5px;
  align-items: center;
  color: #246bfe;
  border: 0;
  background: transparent;
  font-size: 12px;
  font-weight: 600;
}

.personal-api__tutorial-entry button:hover {
  color: #1558dd;
  text-decoration: underline;
}

.personal-api__tutorial-entry i {
  margin-left: 2px;
  font-size: 16px;
  font-style: normal;
  line-height: 1;
}

.personal-api__binding-exchange {
  display: flex;
  height: 54px;
  padding: 0 14px;
  margin-bottom: 15px;
  align-items: center;
  color: #4f5668;
  border: 1px solid #e7eaf1;
  border-radius: 9px;
  background: #f7f8fb;
}

.personal-api__binding-logo {
  display: grid;
  width: 32px;
  height: 32px;
  margin-right: 10px;
  place-items: center;
  overflow: hidden;
  border-radius: 8px;
  background: #f2f6fd;
}

.personal-api__binding-logo img {
  display: block;
  width: 22px;
  height: 22px;
  object-fit: contain;
}

.personal-api__binding-exchange strong {
  min-width: 0;
  overflow: hidden;
  font-size: 13.5px;
  font-weight: 500;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.personal-api__binding-exchange > button {
  padding: 4px 0;
  margin-left: auto;
  color: #9aa2b1;
  border: 0;
  background: transparent;
  font-size: 12px;
  font-weight: 400;
  white-space: nowrap;
}

.personal-api__binding-exchange > button:hover {
  color: #246bfe;
}

.personal-confirm {
  width: min(100%, 430px);
  text-align: center;
}

.personal-confirm__icon {
  display: grid;
  width: 52px;
  height: 52px;
  place-items: center;
  margin: 0 auto 14px;
  color: #d92d20;
  border-radius: 50%;
  background: #fceae8;
  font-size: 25px;
}

.personal-confirm p {
  margin: 10px 0 20px;
  color: #79808f;
  font-size: 13px;
  line-height: 1.7;
}

.personal-confirm--bind-warning {
  width: min(100%, 500px);
  text-align: left;
}

.personal-confirm--bind-warning h3 {
  margin: 0;
  text-align: center;
}

.personal-confirm--bind-warning .bind-warning__message {
  margin: 20px 0 0;
  color: #3a3f4e;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.8;
}

.bind-warning__details {
  margin-top: 20px;
}

.personal-confirm--bind-warning .bind-warning__discount {
  margin: 0;
  color: #5a6072;
  font-size: 13px;
  line-height: 1.7;
}

.bind-warning__discount strong {
  margin: 0 2px;
  color: #e5484d;
  font-size: 18px;
  font-weight: 500;
}

.bind-warning__hint {
  margin-top: 14px;
  color: #8a90a0;
  font-size: 12px;
  line-height: 1.65;
}

.bind-warning__hint > span {
  display: block;
}

.bind-warning__link-row {
  display: grid;
  align-items: flex-start;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 12px;
}

.bind-warning__link-row > span {
  min-width: 0;
  word-break: break-all;
}

.bind-warning__link-row button {
  padding: 0;
  color: #1f6bff;
  border: 0;
  background: transparent;
  cursor: pointer;
  font-size: 12px;
  white-space: nowrap;
}

.bind-warning__actions {
  margin-top: 20px;
}

.personal-modal-fade-enter-active,
.personal-modal-fade-leave-active {
  transition: opacity .18s ease;
}

.personal-modal-fade-enter,
.personal-modal-fade-leave-to {
  opacity: 0;
}

@media (max-width: 1080px) {
  .personal-center__layout {
    grid-template-columns: 1fr;
  }

  .personal-center__side {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  }

  .personal-api__unbound-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 720px) {
  .personal-center__side {
    display: flex;
  }

  .personal-profile__summary,
  .personal-api__card-main,
  .personal-api__heading {
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .personal-profile__summary > .personal-button,
  .personal-api__actions {
    margin-left: 74px;
  }

  .personal-api__support {
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .personal-api__support-list {
    order: 3;
    width: 100%;
  }

  .personal-api__unbound-grid {
    grid-template-columns: 1fr;
  }

  .personal-api__permissions {
    align-items: flex-start;
    flex-direction: column;
  }

  .personal-api__permissions > div {
    margin-left: 0;
  }
}
</style>
